import { useEffect, useState } from "react";
import { Switch } from "@headlessui/react"; // optional: lightweight and accessible toggle
import {
  useUpdateWalletPreferenceMutation,
  useGetWalletPreferenceQuery,
} from "../../redux/services/Wallet";
import { toast } from "sonner";


const AlertPreferences = () => {
  const [preferences, setPreferences] = useState({
    notifyOnTransaction: true,
    alertOnSuccess: true,
    alertOnFailure: true,
    disableAll: false,
  });
  const [updatePreference, { isLoading: saving }] =
    useUpdateWalletPreferenceMutation();
const {
  data: preferenceData,
  isLoading: loadingPreference,
  refetch,
} = useGetWalletPreferenceQuery();

useEffect(() => {
  if (preferenceData?.data) {
    const prefs = preferenceData.data;
    setPreferences({
      notifyOnTransaction: Boolean(prefs.notify_on_transaction),
      alertOnSuccess: Boolean(prefs.notify_on_success),
      alertOnFailure: Boolean(prefs.notify_on_failure),
      disableAll:
        !prefs.notify_on_transaction &&
        !prefs.notify_on_success &&
        !prefs.notify_on_failure,
    });
  }
}, [preferenceData]);

  // handle toggles
  const handleToggle = (key: keyof typeof preferences) => {
    setPreferences((prev) => {
      if (key === "disableAll") {
        // If "Disable all" is toggled on, turn off all others
        return {
          notifyOnTransaction: false,
          alertOnSuccess: false,
          alertOnFailure: false,
          disableAll: !prev.disableAll,
        };
      } else {
        // normal toggle behavior
        return {
          ...prev,
          [key]: !prev[key],
          disableAll: false, // re-enable if user toggles any back on
        };
      }
    });
  };

 const handleSave = async () => {
   try {
     const payload = {
       notify_on_success: preferences.alertOnSuccess,
       notify_on_failure: preferences.alertOnFailure,
       notify_on_transaction: preferences.notifyOnTransaction,
     };

     const res = await updatePreference(payload).unwrap();

     // cast to any to safely check for an optional message from the API,
     // otherwise fall back to a default success message
     const message =
       (res as any)?.data?.message ??
       (res as any)?.message ??
       "Wallet notification preferences updated successfully.";

     toast.success(message);
       await refetch();
   } catch (err: any) {
     console.error("❌ Failed to update preferences:", err);
     const message =
       err?.data?.error?.message ||
       err?.data?.message ||
       "Failed to update wallet preferences.";
     toast.error(message);
   }
 };


  return (
    <div className="p-6 space-y-6">
      <div className="space-y-4">
        {/* Notify on every wallet transaction */}
        <div className="flex items-center justify-between">
          <span className="text-[16px] text-fleetdark">
            Notify me on every wallet transaction
          </span>
          <Switch
            checked={preferences.notifyOnTransaction}
            onChange={() => handleToggle("notifyOnTransaction")}
            className={`${
              preferences.notifyOnTransaction ? "bg-fleetblue" : "bg-gray-300"
            } relative inline-flex h-6 w-11 items-center rounded-full transition`}
          >
            <span
              className={`${
                preferences.notifyOnTransaction
                  ? "translate-x-6"
                  : "translate-x-1"
              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
            />
          </Switch>
        </div>

        {/* Alert on success */}
        <div className="flex items-center justify-between">
          <span className="text-[16px] text-fleetdark">
            Alert me when transaction is successful
          </span>
          <Switch
            checked={preferences.alertOnSuccess}
            onChange={() => handleToggle("alertOnSuccess")}
            className={`${
              preferences.alertOnSuccess ? "bg-fleetblue" : "bg-gray-300"
            } relative inline-flex h-6 w-11 items-center rounded-full transition`}
          >
            <span
              className={`${
                preferences.alertOnSuccess ? "translate-x-6" : "translate-x-1"
              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
            />
          </Switch>
        </div>

        {/* Alert on failure */}
        <div className="flex items-center justify-between">
          <span className="text-[16px] text-fleetdark">
            Alert me when transaction fails
          </span>
          <Switch
            checked={preferences.alertOnFailure}
            onChange={() => handleToggle("alertOnFailure")}
            className={`${
              preferences.alertOnFailure ? "bg-fleetblue" : "bg-gray-300"
            } relative inline-flex h-6 w-11 items-center rounded-full transition`}
          >
            <span
              className={`${
                preferences.alertOnFailure ? "translate-x-6" : "translate-x-1"
              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
            />
          </Switch>
        </div>

        {/* Disable all */}
        <div className="flex items-center justify-between">
          <span className="text-[16px] text-fleetdark">
            Disable all wallet-related notifications
          </span>
          <Switch
            checked={preferences.disableAll}
            onChange={() => handleToggle("disableAll")}
            className={`${
              preferences.disableAll ? "bg-fleetblue" : "bg-gray-300"
            } relative inline-flex h-6 w-11 items-center rounded-full transition`}
          >
            <span
              className={`${
                preferences.disableAll ? "translate-x-6" : "translate-x-1"
              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
            />
          </Switch>
        </div>
      </div>

      {/* Save button */}
      <button
        onClick={handleSave}
        disabled={saving}
        className={`px-6 py-2 rounded-md font-medium transition-colors ${
          saving
            ? "bg-gray-400 text-white cursor-not-allowed"
            : "bg-fleetblue text-white hover:bg-blue-800"
        }`}
      >
        {saving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

export default AlertPreferences;
